import { Graphics } from 'pixi.js';
import type { ColorRamp } from './colors';
import { TILE_WIDTH, TILE_HEIGHT } from '../iso/IsoGrid';

/**
 * Isometric drawing primitives.
 *
 * COORDINATE SYSTEM:
 * - All positions are screen-space pixels
 * - (cx, cy) = center of a floor tile (from worldToScreen)
 * - "elevation" = pixels upward on screen (negative Y direction)
 *
 * TILE GEOMETRY:
 * A floor tile is a diamond: 64px wide, 32px tall (2:1 ratio)
 * Its 4 corners relative to center (cx, cy):
 *   Top:    (cx,      cy - 16)
 *   Right:  (cx + 32, cy)
 *   Bottom: (cx,      cy + 16)
 *   Left:   (cx - 32, cy)
 *
 * A "sub-tile" diamond scaled by factor s (0 to 1):
 *   Top:    (cx,          cy - 16*s)
 *   Right:  (cx + 32*s,   cy)
 *   Bottom: (cx,          cy + 16*s)
 *   Left:   (cx - 32*s,   cy)
 *
 * BOX = top diamond face + two visible side faces, extruded downward.
 * The top face sits at "elevation" pixels above the base position.
 */

const TW = TILE_WIDTH;   // 64
const TH = TILE_HEIGHT;  // 32

/** Draw a flat isometric diamond (floor tile). */
export function drawFloorDiamond(
  g: Graphics,
  cx: number,
  cy: number,
  ramp: ColorRamp,
  alpha: number = 1.0,
): void {
  g.poly([cx, cy - TH / 2, cx + TW / 2, cy, cx, cy + TH / 2, cx - TW / 2, cy]);
  g.fill({ color: ramp.base, alpha });
  g.moveTo(cx, cy - TH / 2);
  g.lineTo(cx - TW / 2, cy);
  g.stroke({ color: ramp.highlight, width: 1, alpha: alpha * 0.6 });
  g.moveTo(cx + TW / 2, cy);
  g.lineTo(cx, cy + TH / 2);
  g.stroke({ color: ramp.shadow, width: 1, alpha: alpha * 0.6 });
}

/**
 * Draw an isometric box sitting on a tile.
 *
 * @param g - Graphics context
 * @param cx - Screen X of tile center
 * @param cy - Screen Y of tile center
 * @param scale - Size relative to tile (1.0 = fills entire tile, 0.5 = half tile)
 * @param height - Height in pixels (screen-space upward)
 * @param elevation - Base elevation in pixels above the tile surface
 * @param ramp - Color ramp (highlight=top, base=left, shadow=right)
 */
export function drawIsoBox(
  g: Graphics,
  cx: number,
  cy: number,
  scale: number,
  height: number,
  elevation: number,
  ramp: ColorRamp,
): void {
  // Diamond half-widths at this scale
  const hw = (TW / 2) * scale; // horizontal half-extent
  const hh = (TH / 2) * scale; // vertical half-extent

  // Base Y (bottom of the box, lifted by elevation)
  const by = cy - elevation;
  // Top Y (top of the box)
  const ty = by - height;

  // Top face diamond (at ty level)
  g.poly([
    cx, ty - hh,        // top point
    cx + hw, ty,        // right point
    cx, ty + hh,        // bottom point
    cx - hw, ty,        // left point
  ]);
  g.fill({ color: ramp.highlight });

  // Left face (bottom-left visible side)
  g.poly([
    cx - hw, ty,        // top-left (of this face)
    cx, ty + hh,        // top-right
    cx, by + hh,        // bottom-right
    cx - hw, by,        // bottom-left
  ]);
  g.fill({ color: ramp.base });

  // Right face (bottom-right visible side)
  g.poly([
    cx + hw, ty,        // top-right
    cx, ty + hh,        // top-left
    cx, by + hh,        // bottom-left
    cx + hw, by,        // bottom-right
  ]);
  g.fill({ color: ramp.shadow });

  // Top face outline
  g.poly([cx, ty - hh, cx + hw, ty, cx, ty + hh, cx - hw, ty]);
  g.stroke({ color: ramp.dark, width: 0.5 });
}

/**
 * Draw an isometric box with independent X and Y scale.
 * scaleX controls width along the left-right iso axis.
 * scaleY controls depth along the top-bottom iso axis.
 */
export function drawIsoBoxXY(
  g: Graphics,
  cx: number,
  cy: number,
  scaleX: number,
  scaleY: number,
  height: number,
  elevation: number,
  ramp: ColorRamp,
): void {
  const hw = (TW / 2) * scaleX;
  const hh = (TH / 2) * scaleY;
  const by = cy - elevation;
  const ty = by - height;

  // Top face
  g.poly([cx, ty - hh, cx + hw, ty, cx, ty + hh, cx - hw, ty]);
  g.fill({ color: ramp.highlight });

  // Left face
  g.poly([cx - hw, ty, cx, ty + hh, cx, by + hh, cx - hw, by]);
  g.fill({ color: ramp.base });

  // Right face
  g.poly([cx + hw, ty, cx, ty + hh, cx, by + hh, cx + hw, by]);
  g.fill({ color: ramp.shadow });

  // Top outline
  g.poly([cx, ty - hh, cx + hw, ty, cx, ty + hh, cx - hw, ty]);
  g.stroke({ color: ramp.dark, width: 0.5 });
}

/**
 * Draw an isometric rectangle — a world-space rectangle aligned to world axes.
 * Unlike drawIsoBox/drawIsoBoxXY which always produce diamond top-faces,
 * this produces a parallelogram where the long edges run along one world axis.
 *
 * @param worldW - Width in world units along the world-X axis (right-down on screen)
 * @param worldD - Depth in world units along the world-Y axis (left-down on screen)
 * @param height - Height in screen pixels
 * @param elevation - Screen pixels above base
 *
 * One world unit = one tile = 64x32 screen pixels.
 * So worldW=0.8, worldD=0.3 is a desk-sized rectangle.
 */
export function drawIsoRect3D(
  g: Graphics,
  cx: number,
  cy: number,
  worldW: number,
  worldD: number,
  height: number,
  elevation: number,
  ramp: ColorRamp,
): void {
  // World-to-screen basis vectors (per unit):
  //   World X → screen (+32, +16)  (right-down)
  //   World Y → screen (-32, +16)  (left-down)
  // Rectangle centered on (cx, cy), extending ±halfW along X, ±halfD along Y

  const halfW = worldW / 2;
  const halfD = worldD / 2;

  // 4 corners in world space: (-halfW, -halfD), (+halfW, -halfD), (+halfW, +halfD), (-halfW, +halfD)
  // Transform to screen offsets:
  const corners = [
    { wx: -halfW, wy: -halfD }, // back-left
    { wx: +halfW, wy: -halfD }, // back-right
    { wx: +halfW, wy: +halfD }, // front-right
    { wx: -halfW, wy: +halfD }, // front-left
  ].map(c => ({
    sx: cx + c.wx * 32 + c.wy * -32,
    sy: cy + c.wx * 16 + c.wy * 16,
  }));

  const by = -elevation;
  const ty = by - height;

  // Top face
  g.poly(corners.map(c => [c.sx, c.sy + ty]).flat());
  g.fill({ color: ramp.highlight });

  // Front-left face (visible: from front-left to front-right, going down)
  const fl = corners[3]!;
  const fr = corners[2]!;
  g.poly([fl.sx, fl.sy + ty, fr.sx, fr.sy + ty, fr.sx, fr.sy + by, fl.sx, fl.sy + by]);
  g.fill({ color: ramp.base });

  // Front-right face (visible: from back-right to front-right, going down)
  const br = corners[1]!;
  g.poly([br.sx, br.sy + ty, fr.sx, fr.sy + ty, fr.sx, fr.sy + by, br.sx, br.sy + by]);
  g.fill({ color: ramp.shadow });

  // Top outline
  g.poly(corners.map(c => [c.sx, c.sy + ty]).flat());
  g.stroke({ color: ramp.dark, width: 0.5 });
}

/**
 * Draw a rectangular table/desk surface.
 * Unlike drawIsoBox which makes diamond top-faces, this draws a
 * parallelogram top face that looks like a rectangle viewed in iso.
 *
 * The desk runs along the left-right iso axis (screen-horizontal).
 * Width = extent along that axis, depth = extent front-to-back.
 *
 * @param width - half-width in pixels along the horizontal screen axis
 * @param depth - half-depth in pixels along the iso depth axis (screen diagonal)
 * @param height - height in pixels
 * @param elevation - base elevation
 */
export function drawIsoTable(
  g: Graphics,
  cx: number,
  cy: number,
  width: number,
  depth: number,
  height: number,
  elevation: number,
  ramp: ColorRamp,
): void {
  const by = cy - elevation;
  const ty = by - height;

  // Top face: parallelogram
  // The 4 corners: take the center, offset by ±width horizontally
  // and ±depth along the iso depth axis (which is ±depth/2 in X, ±depth in Y)
  const dx = depth / 2; // iso depth X component

  g.poly([
    cx - width + dx, ty - depth,  // back-left
    cx + width + dx, ty - depth,  // back-right
    cx + width - dx, ty + depth,  // front-right
    cx - width - dx, ty + depth,  // front-left
  ]);
  g.fill({ color: ramp.highlight });

  // Front face
  g.poly([
    cx - width - dx, ty + depth,  // top-left
    cx + width - dx, ty + depth,  // top-right
    cx + width - dx, by + depth,  // bottom-right
    cx - width - dx, by + depth,  // bottom-left
  ]);
  g.fill({ color: ramp.base });

  // Right face
  g.poly([
    cx + width + dx, ty - depth,  // top-back
    cx + width - dx, ty + depth,  // top-front
    cx + width - dx, by + depth,  // bottom-front
    cx + width + dx, by - depth,  // bottom-back
  ]);
  g.fill({ color: ramp.shadow });

  // Top outline
  g.poly([
    cx - width + dx, ty - depth,
    cx + width + dx, ty - depth,
    cx + width - dx, ty + depth,
    cx - width - dx, ty + depth,
  ]);
  g.stroke({ color: ramp.dark, width: 0.5 });
}

/**
 * Draw a cylinder sitting on a tile (pot, mug, water bottle).
 * Uses ellipse for top/bottom and straight sides.
 *
 * @param cx, cy - Tile center
 * @param radius - Radius as fraction of tile half-width (0-1)
 * @param height - Height in pixels
 * @param elevation - Pixels above tile surface
 * @param ramp - Colors
 */
export function drawIsoCylinder(
  g: Graphics,
  cx: number,
  cy: number,
  radius: number,
  height: number,
  elevation: number,
  ramp: ColorRamp,
): void {
  const rx = (TW / 2) * radius; // screen X radius
  const ry = (TH / 2) * radius; // screen Y radius (half of X for iso)
  const by = cy - elevation;
  const ty = by - height;

  // Body
  g.rect(cx - rx, ty, rx * 2, height);
  g.fill({ color: ramp.base });

  // Left highlight
  g.rect(cx - rx, ty, Math.max(2, rx * 0.2), height);
  g.fill({ color: ramp.highlight });

  // Right shadow
  g.rect(cx + rx - Math.max(2, rx * 0.2), ty, Math.max(2, rx * 0.2), height);
  g.fill({ color: ramp.shadow });

  // Top ellipse
  g.ellipse(cx, ty, rx, ry);
  g.fill({ color: ramp.highlight });
  g.stroke({ color: ramp.dark, width: 0.5 });

  // Bottom rim (only draw if elevated, otherwise it's hidden by the floor)
  if (elevation > 1) {
    g.ellipse(cx, by, rx, ry);
    g.stroke({ color: ramp.dark, width: 0.5 });
  }
}
